import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { Docket } from '../../models';
import { SignatureCaptureDialogComponent } from '../signature-capture/signature-capture-dialog/signature-capture-dialog.component';
import {
  catchError,
  filter,
  map,
  mergeMap,
  startWith,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import {
  LS_DEVICE_CODE_NAME,
  SignatureService
} from '../services/signature.service';
import { EMPTY, merge, of, ReplaySubject, Subject } from 'rxjs';
import { SetTerminalCodeComponent } from '../dialogs/set-terminal-code-dialog/set-terminal-code.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { SignatureListComponent } from '../signature-list/signature-list.component';
import { ConfigService } from '../services/config.service';
import { Booking } from 'src/app/models/booking';
import { SaleDocket } from 'src/app/models/sale-docket';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { NgIf, AsyncPipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';

@Component({
  selector: 'app-signature-section',
  templateUrl: './signature-section.component.html',
  providers: [SignatureService],
  standalone: true,
  imports: [
    FlexModule,
    NgIf,
    MatIconButton,
    MatIcon,
    SignatureListComponent,
    MatButton,
    AsyncPipe
  ]
})
export class SignatureSectionComponent implements OnDestroy {
  @Input() set docket(docket: Docket) {
    this.docketSubject.next(docket);
  }

  @Input() set booking(booking: Booking) {
    this.bookingSubject.next(booking);
  }

  @Input() set saleDocket(saleDocket: SaleDocket) {
    this.saleSubject.next(saleDocket);
  }

  @Input() stage:
    | 'Hire Contract'
    | 'Invoice'
    | 'Sale'
    | 'Delivery'
    | 'Collection'
    | 'Booking'
    | 'Quotation';

  ngUnsubscribe = new Subject<void>();
  signaturesOnScreen = this.configService.userConfig.pipe(
    filter((config) => Boolean(config) && 'signature_on_screen' in config),
    map((config) => Boolean(config.signature_on_screen)),
    startWith(true)
  );
  @ViewChild(SignatureListComponent) signatureList: SignatureListComponent;
  private docketSubject = new ReplaySubject<Docket>(1);
  private bookingSubject = new ReplaySubject<Booking>(1);
  private saleSubject = new ReplaySubject<SaleDocket>(1);

  signable$ = merge(
    this.docketSubject.asObservable().pipe(
      filter((docket) => Boolean(docket)),
      map((docket) => ({
        type: 'docket',
        id: docket.id
      }))
    ),
    this.bookingSubject.asObservable().pipe(
      filter((booking) => Boolean(booking)),
      map((booking) => ({
        type: 'booking',
        id: booking.id
      }))
    ),
    this.saleSubject.asObservable().pipe(
      filter((sale) => Boolean(sale)),
      map((sale) => ({
        type: 'saleDocket',
        id: sale.id
      }))
    )
  );

  constructor(
    private signatureService: SignatureService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private configService: ConfigService
  ) {}

  private sendSignatureRequest(code: string) {
    return this.signable$.pipe(
      take(1),
      mergeMap((signable) => {
        return this.signatureService.requestSignature({
          documentType: signable.type as 'docket' | 'booking' | 'saleDocket',
          documentId: signable.id,
          code
        });
      })
    );
  }

  openSignatureDialog() {
    merge(
      this.docketSubject.pipe(
        take(1),
        map((docket) => ({
          type: 'docket',
          id: docket.id,
          stage: this.stage
        }))
      ),
      this.bookingSubject.pipe(
        take(1),
        map((booking) => ({
          type: 'booking',
          id: booking.id,
          stage: this.stage
        }))
      ),
      this.saleSubject.pipe(
        take(1),
        map((saleDocket) => ({
          type: 'saleDocket',
          id: saleDocket.id,
          stage: this.stage
        }))
      )
    )
      .pipe(
        mergeMap((payload) => {
          return this.dialog
            .open(SignatureCaptureDialogComponent, {
              data: payload,
              maxHeight: '80vh',
              maxWidth: '100vw'
            })
            .afterClosed()
            .pipe(take(1));
        })
      )
      .subscribe((result) => {
        if (result) {
          this.signatureList.refreshSignatures.next();
        }
      });
  }

  requestSignature() {
    this.getDeviceCode()
      .pipe(
        mergeMap((code) => this.sendSignatureRequest(code)),
        catchError((error, o) => {
          if (error?.status === 404) {
            // remove from localstorage and try again
            localStorage.removeItem(LS_DEVICE_CODE_NAME);
            return this.getDeviceCode().pipe(
              mergeMap((code) => this.sendSignatureRequest(code))
            );
          }
          throw 'Unable to register with notification service when trying to collect signature';
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (response) => {},
        error: (error) => this.toastrService.error(error)
      });
  }

  private getDeviceCode(forceDialog = false) {
    const code = localStorage.getItem(LS_DEVICE_CODE_NAME);
    if (code) {
      return of(code);
    }
    return this.showDeviceSetupDialog();
  }

  private showDeviceSetupDialog() {
    return this.dialog
      .open(SetTerminalCodeComponent, { maxWidth: 600 })
      .afterClosed()
      .pipe(
        tap((result: string | null) => {
          if (result) {
            localStorage.setItem(LS_DEVICE_CODE_NAME, result);
            return result;
          }
          throw 'Code not provided';
        }),
        take(1),
        filter((code) => Boolean(code)),
        catchError((e) => {
          return EMPTY;
        })
      );
  }

  openDeviceSetup() {
    return this.showDeviceSetupDialog().subscribe();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
